import React, { useState, useMemo, useEffect } from 'react';
import { Layout as AntLayout, Menu, Typography, Drawer, Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  ShopOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Logo from './Logo';
import ProfileMenu from './ProfileMenu';

const { Header, Sider, Content } = AntLayout;
const { Title } = Typography;

const SIDEBAR_COLOR = '#001350';

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();

  // Check if the screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setDrawerVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Special handling for dashboard - it can be accessed via both '/' and '/dashboard'
  const isDashboardRoute = location.pathname === '/' || location.pathname === '/dashboard';

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>,
      title: 'Dashboard',
    },
    {
      key: '/orders',
      icon: <ShoppingCartOutlined />,
      label: <Link to="/orders">Bestellingen</Link>,
      title: 'Bestellingen',
    },
    {
      key: '/producten',
      icon: <ShopOutlined />,
      label: <Link to="/producten">Producten</Link>,
      title: 'Producten',
    },
    {
      key: '/facturen',
      icon: <FileTextOutlined />,
      label: <Link to="/facturen">Facturen</Link>,
      title: 'Facturen',
    },
  ];

  const currentPageTitle = useMemo(() => {
    // Special handling for dashboard routes
    if (isDashboardRoute) {
      return 'Dashboard';
    }
    
    const currentItem = menuItems.find(item => item.key === location.pathname);
    return currentItem?.title || '';
  }, [location.pathname, isDashboardRoute]);

  // Mobile drawer menu
  const renderMobileMenu = () => (
    <Drawer
      title={<Logo height={32} collapsed={false} />}
      placement="left"
      onClose={() => setDrawerVisible(false)}
      open={drawerVisible}
      bodyStyle={{ padding: 0, backgroundColor: SIDEBAR_COLOR }}
      headerStyle={{ backgroundColor: SIDEBAR_COLOR, color: 'white' }}
    >
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[isDashboardRoute ? 'dashboard' : location.pathname]}
        items={menuItems}
        style={{ backgroundColor: SIDEBAR_COLOR }}
        onClick={() => setDrawerVisible(false)}
      />
    </Drawer>
  );

  // Desktop sidebar
  const renderDesktopSidebar = () => (
    <Sider 
      trigger={null} 
      collapsible 
      collapsed={collapsed}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: SIDEBAR_COLOR,
        zIndex: 999,
      }}
    >
      <div style={{ 
        padding: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '64px',
        backgroundColor: SIDEBAR_COLOR,
      }}>
        <Logo height={collapsed ? 24 : 32} collapsed={collapsed} />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[isDashboardRoute ? 'dashboard' : location.pathname]}
        items={menuItems}
        style={{
          backgroundColor: SIDEBAR_COLOR,
        }}
      />
    </Sider>
  );

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {isMobile ? renderMobileMenu() : renderDesktopSidebar()}
      
      <AntLayout style={{ 
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), 
        transition: 'margin-left 0.2s' 
      }}>
        <Header style={{ 
          padding: '0 16px',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isMobile ? (
              <Button 
                type="text"
                icon={<MenuOutlined />} 
                onClick={() => setDrawerVisible(true)}
                style={{ 
                  fontSize: '16px',
                  color: SIDEBAR_COLOR,
                  marginRight: '16px',
                }}
              />
            ) : (
              React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                onClick: () => setCollapsed(!collapsed),
                style: { 
                  fontSize: '16px',
                  color: SIDEBAR_COLOR,
                  cursor: 'pointer',
                  marginRight: '24px',
                },
              })
            )}
            <Title level={4} style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>{currentPageTitle}</Title>
          </div>
          <ProfileMenu />
        </Header>
        <Content style={{ 
          margin: isMobile ? '16px 8px' : '24px 16px',
          padding: isMobile ? 16 : 24,
          background: '#fff',
          borderRadius: '4px',
          minHeight: 280,
        }}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
